// Thonny-like toolbar (spec §6.2). File actions (New/Open/Save/Save As/Download)
// and the API reference are wired in Phase 4; Run/Stop/speed came in Phase 2.

type Speed = "realtime" | "instant";

type Props = {
  speed: Speed;
  onToggleSpeed: () => void;
  fontSize: number;
  onFontSize: (delta: number) => void;
  sidebarOpen: boolean;
  onToggleSidebar: () => void;
  onRun: () => void;
  onStop: () => void;
  canRun: boolean;
  running: boolean;
  // Phase 4 file workflow
  scriptName: string;
  dirty: boolean;
  onNew: () => void;
  onOpen: () => void;
  onSave: () => void;
  onSaveAs: () => void;
  onDownload: () => void;
  onApiRef: () => void;
};

function Btn({
  label,
  onClick,
  variant = "default",
  disabled = false,
  title,
}: {
  label: string;
  onClick: () => void;
  variant?: "default" | "primary" | "danger";
  disabled?: boolean;
  title?: string;
}) {
  const base =
    "rounded px-3 py-1.5 text-sm font-medium transition disabled:opacity-50 disabled:cursor-not-allowed";
  const styles = {
    default: "bg-slate-700 text-slate-100 hover:bg-slate-600",
    primary: "bg-emerald-600 text-white hover:bg-emerald-500",
    danger: "bg-red-600 text-white hover:bg-red-500",
  }[variant];
  return (
    <button
      type="button"
      className={`${base} ${styles}`}
      onClick={onClick}
      disabled={disabled}
      title={title}
    >
      {label}
    </button>
  );
}

export default function Toolbar({
  speed,
  onToggleSpeed,
  fontSize,
  onFontSize,
  sidebarOpen,
  onToggleSidebar,
  onRun,
  onStop,
  canRun,
  running,
  scriptName,
  dirty,
  onNew,
  onOpen,
  onSave,
  onSaveAs,
  onDownload,
  onApiRef,
}: Props) {
  return (
    <div className="flex flex-wrap items-center gap-2 border-b border-slate-700 bg-slate-800 px-3 py-2">
      <Btn label="New" onClick={onNew} />
      <Btn label="Open" onClick={onOpen} />
      <Btn
        label="Save"
        onClick={onSave}
        variant={dirty ? "primary" : "default"}
        title="Save to your account (Ctrl+S)"
      />
      <Btn label="Save As" onClick={onSaveAs} />
      <Btn label="Download .py" onClick={onDownload} title="Download the exact editor buffer" />

      <span className="mx-1 h-6 w-px bg-slate-600" />

      <Btn label="▶ Run" variant="primary" onClick={onRun} disabled={!canRun} />
      <Btn label="■ Stop" variant="danger" onClick={onStop} disabled={!running} />

      <button
        type="button"
        onClick={onToggleSpeed}
        className="rounded bg-slate-700 px-3 py-1.5 text-sm font-medium text-slate-100 hover:bg-slate-600"
        title="Toggle execution speed"
      >
        Speed: {speed === "realtime" ? "Real-time" : "Instant"}
      </button>

      <span className="mx-1 h-6 w-px bg-slate-600" />

      <div className="flex items-center gap-1 text-slate-300">
        <button
          type="button"
          className="rounded bg-slate-700 px-2 py-1 text-sm hover:bg-slate-600"
          onClick={() => onFontSize(-1)}
          aria-label="Decrease font size"
        >
          A−
        </button>
        <span className="w-8 text-center text-xs">{fontSize}px</span>
        <button
          type="button"
          className="rounded bg-slate-700 px-2 py-1 text-sm hover:bg-slate-600"
          onClick={() => onFontSize(1)}
          aria-label="Increase font size"
        >
          A+
        </button>
      </div>

      <span className="mx-1 h-6 w-px bg-slate-600" />

      <Btn label="API reference" onClick={onApiRef} />
      <Btn label={sidebarOpen ? "Hide sensors" : "Show sensors"} onClick={onToggleSidebar} />

      {/* Current filename + unsaved indicator (Thonny-style). */}
      <span
        className="ml-auto flex items-center gap-1 font-mono text-xs text-slate-400"
        title={dirty ? "Unsaved changes" : "Saved"}
      >
        {dirty && <span className="text-amber-400">●</span>}
        {scriptName}
      </span>
    </div>
  );
}
